import { html } from "../../render";
import { attr, cn, spreadAttrs } from "../../utils";
import { LpIcon } from "../icon";
import { LpLoader } from "../loader";
export const LP_BUTTON_VARIANTS = {
    variant: ["primary", "secondary", "ghost", "refresh", "destructive", "secondary-destructive", "outline"],
    size: ["xs", "sm", "base", "lg"],
    shape: ["base", "square", "circle"],
};
export const LP_BUTTON_DEFAULT_VARIANTS = {
    variant: "secondary",
    size: "base",
    shape: "base",
};
const buttonManagedAttrs = [
    "data-lp-component",
    "class",
    "data-variant",
    "data-size",
    "data-shape",
    "data-bleed",
    "aria-label",
    "href",
    "target",
    "rel",
    "aria-disabled",
    "aria-busy",
    "tabindex",
    "type",
    "disabled",
];
function getComputedRel(rel, target) {
    if (rel) {
        return rel;
    }
    return target === "_blank" ? "noopener noreferrer" : undefined;
}
function isDisabledAttribute(value) {
    return value === true || value === "true" || value === "disabled";
}
function renderButtonContent({ label, variant, size, prefixIcon, prefixIconWeight, suffixIcon, suffixIconWeight, loading = false, }) {
    const isEmphasis = variant === "primary" || variant === "destructive";
    return html `
        ${isEmphasis
        ? html `<span class="lp-button_emphasis" aria-hidden="true"></span>`
        : ""}
        <span class="lp-button_inner">
            ${loading
        ? LpLoader({
            size: size === "lg" ? 16 : 14,
            className: "lp-button_icon lp-button_loader",
        })
        : ""}
            ${loading
        ? ""
        : LpIcon({
            name: prefixIcon,
            weight: prefixIconWeight,
            className: "lp-button_icon lp-button_prefix",
        })}
            ${label ? html `<span class="lp-button_label">${label}</span>` : ""}
            ${loading
        ? ""
        : LpIcon({
            name: suffixIcon,
            weight: suffixIconWeight,
            className: "lp-button_icon lp-button_suffix",
        })}
        </span>
    `;
}
export function LpButton({ label, ariaLabel, type = "button", variant = LP_BUTTON_DEFAULT_VARIANTS.variant, size = LP_BUTTON_DEFAULT_VARIANTS.size, shape = LP_BUTTON_DEFAULT_VARIANTS.shape, prefixIcon, prefixIconWeight, suffixIcon, suffixIconWeight, disabled = false, loading = false, bleed = false, className, attributes, }) {
    const isDisabled = disabled
        || loading
        || isDisabledAttribute(attributes?.disabled)
        || isDisabledAttribute(attributes?.["aria-disabled"]);
    const classValue = cn("lp-button", className, attributes?.class);
    return html `
        <button
            data-lp-component="Button"
            class="${classValue}"
            ${attr("data-variant", variant)}
            ${attr("data-size", size)}
            ${attr("data-shape", shape)}
            ${attr("data-bleed", bleed)}
            ${attr("aria-label", ariaLabel)}
            ${attr("type", type)}
            ${attr("disabled", isDisabled)}
            ${attr("aria-busy", loading ? "true" : undefined)}
            ${spreadAttrs(attributes, { omit: buttonManagedAttrs })}
        >
            ${renderButtonContent({
        label,
        variant,
        size,
        prefixIcon,
        prefixIconWeight,
        suffixIcon,
        suffixIconWeight,
        loading,
    })}
        </button>
    `;
}
export function LpLinkButton({ label, ariaLabel, href, target, rel, variant = LP_BUTTON_DEFAULT_VARIANTS.variant, size = LP_BUTTON_DEFAULT_VARIANTS.size, shape = LP_BUTTON_DEFAULT_VARIANTS.shape, prefixIcon, prefixIconWeight, suffixIcon, suffixIconWeight, disabled = false, bleed = false, className, attributes, }) {
    const isDisabled = disabled
        || isDisabledAttribute(attributes?.["aria-disabled"]);
    return html `
        <a
            data-lp-component="LinkButton"
            class="${cn("lp-button", className, attributes?.class)}"
            ${attr("data-variant", variant)}
            ${attr("data-size", size)}
            ${attr("data-shape", shape)}
            ${attr("data-bleed", bleed)}
            ${attr("aria-label", ariaLabel)}
            ${attr("href", isDisabled ? undefined : href)}
            ${attr("target", isDisabled ? undefined : target)}
            ${attr("rel", isDisabled ? undefined : getComputedRel(rel, target))}
            ${attr("aria-disabled", isDisabled ? "true" : undefined)}
            ${attr("tabindex", isDisabled ? "-1" : undefined)}
            ${spreadAttrs(attributes, { omit: buttonManagedAttrs })}
        >
            ${renderButtonContent({
        label,
        variant,
        size,
        prefixIcon,
        prefixIconWeight,
        suffixIcon,
        suffixIconWeight,
    })}
        </a>
    `;
}
